import { forwardRef } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

// White card: radius 20, soft shadow, padding 24 (20 on phones) or "sm" 16.
// `interactive` makes the whole card a keyboard-accessible target (pass onClick).
const GrCard = forwardRef(function GrCard(
  {
    as = "section",
    padding = "md",
    interactive = false,
    onClick,
    children,
    sx,
    ...rest
  },
  ref
) {
  const pad = padding === "sm" ? gr.space[4] : gr.space[6];
  const handleKeyDown = (e) => {
    if (interactive && onClick && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onClick(e);
    }
  };
  return (
    <Box
      ref={ref}
      component={as}
      onClick={onClick}
      onKeyDown={interactive ? handleKeyDown : undefined}
      tabIndex={interactive ? 0 : undefined}
      role={interactive && as !== "button" && as !== "a" ? "button" : undefined}
      sx={{
        minWidth: 0,
        background: gr.color.surface,
        borderRadius: gr.radius.card,
        boxShadow: gr.shadow.card,
        border: "1px solid transparent",
        padding: pad,
        color: gr.color.ink,
        fontFamily: gr.font.family,
        ...(padding !== "sm"
          ? { "@media (max-width: 599.95px)": { padding: gr.space[5] } }
          : {}),
        ...(interactive
          ? {
              cursor: "pointer",
              transition: "border-color .15s ease, box-shadow .15s ease",
              "&:hover": { borderColor: gr.color.line },
              "&:focus-visible": {
                outline: "none",
                borderColor: gr.color.brand,
                boxShadow: gr.shadow.focus,
              },
            }
          : {}),
        ...sx,
      }}
      {...rest}
    >
      {children}
    </Box>
  );
});

GrCard.propTypes = {
  as: PropTypes.oneOf(["section", "article", "div", "li", "a", "button"]),
  padding: PropTypes.oneOf(["md", "sm"]),
  interactive: PropTypes.bool,
  onClick: PropTypes.func,
  children: PropTypes.node,
  sx: PropTypes.object,
};

export default GrCard;
